import React from 'react';

/**
 * Higher order component to wrap a step with requisition logic.
 *
 * @param RequisitionComponent the component to wrap
 */
function withRequisitionStep(RequisitionComponent) {

	return class extends React.Component {
		/**
		 * Constructor sets instance variables and binds functions.
		 *
		 * @param props the properties
		 */
		constructor(props) {
			super(props);

			/* The Gear requisition used to hold and display selections */
			let activeGearRequisition = null;
	
			if( this.props.activeGearRequisitionId && !this.isShipToOfficeOrder( this.props.gearOrder ) ) {
				activeGearRequisition = this.getRequisitionFromOrder( this.props.gearOrder, this.props.activeGearRequisitionId );
			}

			this.state = {
				activeGearRequisition: activeGearRequisition,
				loading: true
			}

			this.fetchOmsRequisition = this.fetchOmsRequisition.bind(this);
			this.getRequisitionFromOrder = this.getRequisitionFromOrder.bind(this);
			this.getRequisitionTitle = this.getRequisitionTitle.bind(this);
			this.shouldFetchOmsRequisition = this.shouldFetchOmsRequisition.bind(this);
		}

		/*
		 * When the component first mounts load the omsRequisition if an 
		 * activeGearRequisitionId was passed in
		 */
		componentDidMount() {
			/*
			 * If the gearOrder does not have the practitioner redirect to the practitioner step.
			 */
			if( !this.props.gearOrder.practitioner || !this.props.gearOrder.practitioner.gsdlNo ) {
				this.props.updateStep(this.props.gearOrder, 'panel', 'Back');
			} else if (this.props.gearOrder.dataConfirmed && !this.props.gearOrder.requisitions?.length) {
				/* 
				 * If the order has been confirmed but does not have any requisitions redirect to start a new order.
				 * This prevents a situation where a client could add a new drop-ship to an already confirmed order.
				 */
				this.props.setGearOrder(null);
				this.props.history.push({ pathname: '/' });
			} else {
				// Only fetch the omsRequisition if there isn't already one available or if it doesn't match the active req
				if( this.shouldFetchOmsRequisition()  ) {
					this.props.setLoading(true);
					this.fetchOmsRequisition( this.props.activeGearRequisitionId )
					// .then(x => new Promise(resolve => setTimeout(() => resolve(x), 3000)))
					.then( omsRequisition => {
						// Set parent omsRequisition
						this.props.setOmsRequisition( omsRequisition );
					})
					.catch( e => {
						this.props.showError(e.message);
					})
					.finally( () => {
						this.props.setLoading(false);
						this.setState({loading: false});
					});
				} else {
					this.setState({loading: false});
				}
			}
		}

		/**
		 * Returns a Promise that will fetch and return the OMS requisition.
		 * Any thrown exceptions will be rejected with an Error object containing the error message.
		 *
		 * @param gearReqId The gear requisition id of the requisition to fetch
		 */
		fetchOmsRequisition( gearReqId ) {
			return fetch(process.env.REACT_APP_GEAR_REST_URL+'/requisitionById?gearReqId='+gearReqId, {
				mode: 'cors',
				credentials: 'include',
				method: 'GET',
				timeout: 1000,
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				}
			})
			.then(response => {
				if (response.ok) {
					return response.json();
				} else {
					return Promise.reject({
						response: response
					});
				}
			})
			.catch(e => {
				if (e.response) {
					if (e.response.status === 401) {
						this.props.history.push({pathname:'/login'});
						return;
					} else if (e.response.status === 403) {
						this.props.history.push({pathname:'/', search:'status=non-beta'});
						return;
					} else {
						return e.response.json().then( json => {
							if( json.message ) {
								return Promise.reject(new Error(json.message));
							} else if (e.response.status === 404 || e.response.status === 400){
								return Promise.reject(new Error('We could not find a requisition with that number. Please check the number and contact Genova Diagnostics if you believe it is valid.'));
							} else {
								return Promise.reject(new Error('An unexpected error has occurred. Please call us at 800-522-4762.'));
							}
						});
					}
				} 
				return Promise.reject(new Error('An unexpected error has occurred. Please call us at 800-522-4762.'));
			});
		}

		/**
		 * Finds a Gear requisition object in a Gear order by req id.
		 *
		 * @param gearOrder The gearOrder to search
		 * @param gearReqId the req id to search by
		 */
		getRequisitionFromOrder( gearOrder, gearReqId ) {
			let activeReq = null;
			if( gearOrder && gearOrder.requisitions ) {
				activeReq = gearOrder.requisitions.find( (item) => { 
					return item.id === +gearReqId; 
				});
			}
			return activeReq;
		}

		/**
		 * Gets the displayable title for an oms requisition.
		 * The title may or may not contain a req number, depending on if the 
		 * corresponding gear req is a drop-ship
		 */
		getRequisitionTitle( omsRequisition ) {
			let requisitionTitle = '';
			if( omsRequisition ) {
				if ( omsRequisition.activationCode ) {
					requisitionTitle = 'Activation Number ' + omsRequisition.activationCode + ' - ';
				} else if ( omsRequisition.id ) {
					requisitionTitle = 'Requisition ' + omsRequisition.id + ' - ';
				}
				requisitionTitle += omsRequisition.orderableItem.name;
			}
			return requisitionTitle;
		}

		/**
		 * Determines if we already have the OMS Requisition structure needed for the step.
		 * A new OMS Requisition structure will be needed if there isn't one or if the 
		 * orderable item id of the current one doesn't match the orderable item id of the active
		 * gear req.
		 */
		shouldFetchOmsRequisition() {
			let shouldFetchReq = false;
			// Don't fetch for ship to office orders or when adding a new test to the order
			if( this.isShipToOfficeOrder( this.props.gearOrder ) || this.props.addNewReq ) {
				shouldFetchReq = false;
			} else if( this.props.activeGearRequisitionId ) {
				if( !this.props.omsRequisition ) {
					shouldFetchReq = true;
				} else {
					let activeGearReq = this.getRequisitionFromOrder( this.props.gearOrder, this.props.activeGearRequisitionId );
					let omsReqOrderableItemId = (this.props.omsRequisition.orderableItem || {}).id;
					if( activeGearReq && activeGearReq.omsOrderableItemId && Number(activeGearReq.omsOrderableItemId) !== Number(omsReqOrderableItemId) ) {
						shouldFetchReq = true;
					}
				}
			}
			return shouldFetchReq;
		}

		/**
		 * Returns true if any requisition on the order is a ship-to-office requisition.
		 */
		isShipToOfficeOrder = ( gearOrder ) => {
			return gearOrder && gearOrder.containsShipToOfficeRequisition;
		}

		render() {
			if( this.state.loading ) {
				return (null);
			}
			return (
				<RequisitionComponent
					activeGearRequisition={this.state.activeGearRequisition}
					getRequisitionFromOrder={this.getRequisitionFromOrder}
					getRequisitionTitle={this.getRequisitionTitle}
					isShipToOfficeOrder={this.isShipToOfficeOrder}
					{...this.props} />
			);
		}
	};
}

export default withRequisitionStep;
